// =============================================================================
// File: frontend/src/components/SubscriptionTab.jsx
// Stripe subscription management — single PRO tier, trial, referrals
// Patent claims exercised: P-007 Claims 1-6
// =============================================================================

import React, { useState, useEffect } from 'react';
import { API_BASE } from '../config';

function SubscriptionTab({ fetchWithAuth, setActiveTab, user, refreshUser, embedded }) {
  const [tiers, setTiers] = useState([]);          // [base, pro, max] from /api/billing/plans
  const [creditPacks, setCreditPacks] = useState([]);
  const [creditBalance, setCreditBalance] = useState(null);  // /api/billing/credits/balance
  const [creditUsage, setCreditUsage] = useState(null);      // /api/profile/ai-usage (detail tables, relocated from View My Plan modal)
  const [billingInterval, setBillingInterval] = useState('monthly');
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [referralStats, setReferralStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState('Subscription activated successfully.');
  const [error, setError] = useState(null);
  const [showBuyCredits, setShowBuyCredits] = useState(false);
  const [cancelStep, setCancelStep] = useState(0);  // 0=closed, 1=first confirm, 2=final confirm

  useEffect(() => {
    loadData();
    checkForSuccessRedirect();
  }, []);

  const checkForSuccessRedirect = () => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('subscription') === 'success') {
      setSuccessMsg('Subscription activated successfully. Welcome to Healist.');
      setShowSuccess(true);
      window.history.replaceState({}, '', window.location.pathname);
      if (refreshUser) refreshUser();
      setTimeout(() => setShowSuccess(false), 8000);
    } else if (params.get('credits') === 'success') {
      setSuccessMsg('Credits added to your account. They never expire.');
      setShowSuccess(true);
      window.history.replaceState({}, '', window.location.pathname);
      setTimeout(() => setShowSuccess(false), 8000);
    }
  };

  const loadData = async () => {
    const safeFetch = async (promise) => {
      try { return await promise; } catch { return null; }
    };
    try {
      const [plansRes, statusRes, referralRes, creditsRes, usageRes] = await Promise.all([
        safeFetch(fetch(`${API_BASE}/api/billing/plans`)),
        safeFetch(fetchWithAuth(`${API_BASE}/api/billing/subscription-status`)),
        safeFetch(fetchWithAuth(`${API_BASE}/api/billing/referral/stats`)),
        safeFetch(fetchWithAuth(`${API_BASE}/api/billing/credits/balance`)),
        safeFetch(fetchWithAuth(`${API_BASE}/api/profile/ai-usage`)),
      ]);
      if (plansRes?.ok) {
        const data = await plansRes.json();
        setTiers(data.tiers || []);
        setCreditPacks(data.credit_packs || []);
      }
      if (statusRes?.ok) {
        setSubscriptionStatus(await statusRes.json());
      }
      if (referralRes?.ok) {
        setReferralStats(await referralRes.json());
      }
      if (creditsRes?.ok) {
        setCreditBalance(await creditsRes.json());
      }
      if (usageRes?.ok) {
        setCreditUsage(await usageRes.json());
      }
    } catch (err) {
      setError('Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribeTier = async (tier) => {
    const priceId = billingInterval === 'monthly' ? tier.monthly_price_id : tier.annual_price_id;
    if (!priceId) return;

    setActionLoading(true);
    setError(null);
    try {
      const referral = (() => {
        try { return JSON.parse(sessionStorage.getItem('healist_referral'))?.code; } catch { return null; }
      })();
      const res = await fetchWithAuth(`${API_BASE}/api/billing/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(referral ? { price_id: priceId, referral_code: referral } : { price_id: priceId }),
      });
      if (res.ok) {
        const data = await res.json();
        window.location.href = data.checkout_url;
      } else {
        const errData = await res.json();
        setError(errData.detail || 'Failed to start checkout');
      }
    } catch (err) {
      setError('Failed to connect to payment service');
    } finally {
      setActionLoading(false);
    }
  };

  const handleBuyCredits = async (pack) => {
    if (!pack?.price_id) return;
    setActionLoading(true);
    setError(null);
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/billing/buy-credits`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ price_id: pack.price_id }),
      });
      if (res.ok) {
        const data = await res.json();
        window.location.href = data.checkout_url;
      } else {
        const errData = await res.json();
        setError((errData.detail && errData.detail.message) || errData.detail || 'Failed to start credit purchase');
      }
    } catch (err) {
      setError('Failed to connect to payment service');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSubscribeNow = async () => {
    setActionLoading(true);
    setError(null);
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/billing/subscribe-now`, { method: 'POST' });
      if (res.ok) {
        setSuccessMsg('Your trial has ended — your paid plan is now active.');
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 8000);
        loadData();
        if (refreshUser) refreshUser();
      } else {
        const errData = await res.json();
        setError(errData.detail || 'Failed to start your paid plan');
      }
    } catch (err) {
      setError('Failed to connect to payment service');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    setActionLoading(true);
    setError(null);
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/billing/cancel`, { method: 'POST' });
      if (res.ok) {
        setCancelStep(0);
        setSuccessMsg('Subscription canceled. You keep full access until your paid period ends, and you won’t be charged again.');
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 8000);
        loadData();
        if (refreshUser) refreshUser();
      } else {
        const errData = await res.json();
        setError(errData.detail || 'Failed to cancel subscription');
        setCancelStep(0);
      }
    } catch (err) {
      setError('Failed to connect to payment service');
      setCancelStep(0);
    } finally {
      setActionLoading(false);
    }
  };

  const handleResume = async () => {
    setActionLoading(true);
    setError(null);
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/billing/resume`, { method: 'POST' });
      if (res.ok) {
        setSuccessMsg('Subscription resumed — it will renew as normal.');
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 6000);
        loadData();
        if (refreshUser) refreshUser();
      } else {
        const errData = await res.json();
        setError(errData.detail || 'Failed to resume subscription');
      }
    } catch (err) {
      setError('Failed to connect to payment service');
    } finally {
      setActionLoading(false);
    }
  };

  const handleManageBilling = async () => {
    setActionLoading(true);
    setError(null);
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/billing/create-portal-session`, {
        method: 'POST',
      });
      if (res.ok) {
        const data = await res.json();
        window.location.href = data.portal_url;
      } else {
        const errData = await res.json();
        setError(errData.detail || 'Failed to open billing portal');
      }
    } catch (err) {
      setError('Failed to connect to payment service');
    } finally {
      setActionLoading(false);
    }
  };

  const handleApplyReward = async () => {
    setActionLoading(true);
    setError(null);
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/billing/referral/apply-reward`, {
        method: 'POST',
      });
      if (res.ok) {
        const data = await res.json();
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 5000);
        loadData();
      } else {
        const errData = await res.json();
        setError(errData.detail || 'Failed to apply reward');
      }
    } catch (err) {
      setError('Failed to apply reward');
    } finally {
      setActionLoading(false);
    }
  };

  const [copySuccess, setCopySuccess] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);

  const copyReferralCode = async () => {
    if (!referralStats?.referral_code) return;
    const shareUrl = `${window.location.origin}/?ref=${referralStats.referral_code}`;
    setCopyFailed(false);
    try {
      // navigator.clipboard is undefined in insecure contexts, and writeText REJECTS on
      // iOS Safari outside a user-gesture context. The previous version had no .catch, so a
      // rejection left the button silently doing nothing (and threw an unhandled rejection).
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard API unavailable');
      await navigator.clipboard.writeText(shareUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000);
    } catch {
      // Degrade, never dead-end (feedback_client_never_stricter_than_server): select the
      // link so the user can copy it by hand, and TELL them that is what happened.
      setCopyFailed(true);
      const el = document.getElementById('sub-referralCodeText');
      if (el && window.getSelection && document.createRange) {
        const range = document.createRange();
        range.selectNodeContents(el);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      }
      setTimeout(() => setCopyFailed(false), 8000);
    }
  };

  const currentStatus = subscriptionStatus?.status || user?.subscription_status || 'trial';
  // Single source — backend-resolved user.is_subscriber (#consistency P1/C). The badge
  // below still renders the precise live status; this flag is the account-level truth.
  const isActive = !!user?.is_subscriber;
  const isPermanentTest = user?.permanent_test_acct || subscriptionStatus?.permanent_test_acct;

  const TIER_NAMES = { base: 'Healist Base', pro: 'Healist Pro', max: 'Healist Max' };
  const currentTierKey = subscriptionStatus?.tier || user?.subscription_tier || '';
  const currentTierName = TIER_NAMES[currentTierKey] || (isActive || isPermanentTest ? 'Healist' : 'No Active Plan');
  const cancelPending = !!subscriptionStatus?.cancel_at_period_end;
  const canBuyCredits = !!creditBalance?.can_buy_credits;

  const getStatusBadgeClass = () => {
    if (isPermanentTest) return 'sub-statusBadge sub-statusBadge--active';
    if (currentStatus === 'active') return 'sub-statusBadge sub-statusBadge--active';
    if (currentStatus === 'trialing') return 'sub-statusBadge sub-statusBadge--trial';
    if (currentStatus === 'past_due') return 'sub-statusBadge sub-statusBadge--pastDue';
    if (currentStatus === 'expired' || currentStatus === 'canceled') return 'sub-statusBadge sub-statusBadge--canceled';
    return 'sub-statusBadge';
  };

  const getStatusLabel = () => {
    if (isPermanentTest) return 'Test Account';
    if (currentStatus === 'trialing') return 'Free Trial';
    if (currentStatus === 'active') return 'Active';
    if (currentStatus === 'past_due') return 'Past Due';
    if (currentStatus === 'expired') return 'Expired';
    if (currentStatus === 'canceled') return 'Canceled';
    return currentStatus.charAt(0).toUpperCase() + currentStatus.slice(1);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return null;
    const date = typeof timestamp === 'number'
      ? new Date(timestamp * 1000)
      : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  };

  const getTrialDaysRemaining = () => {
    const trialEnd = subscriptionStatus?.trial_end || user?.trial_ends_at;
    if (!trialEnd) return null;
    const end = typeof trialEnd === 'number' ? new Date(trialEnd * 1000) : new Date(trialEnd);
    const now = new Date();
    return Math.max(0, Math.ceil((end - now) / (1000 * 60 * 60 * 24)));
  };

  if (loading) {
    return (
      <div className={embedded ? '' : 'sub-container'}>
        {!embedded && (
          <div className="page-header-row">
            <h2 className="page-section-title">Subscription</h2>
            <button className="page-exit-btn" onClick={() => setActiveTab('dashboard')}>&#8592; Dashboard</button>
          </div>
        )}
        <div className="card"><div className="card-body p-5">Loading subscription details...</div></div>
      </div>
    );
  }

  return (
    <div className={embedded ? '' : 'sub-container'}>
      {!embedded && (
        <div className="page-header-row">
          <h2 className="page-section-title">Subscription</h2>
          <button className="page-exit-btn" onClick={() => setActiveTab('dashboard')}>&#8592; Dashboard</button>
        </div>
      )}

      {showSuccess && (
        <div className="sub-successBanner">
          {successMsg}
        </div>
      )}

      {error && (
        <div className="sub-errorBanner">
          {typeof error === 'object' ? error.message || JSON.stringify(error) : error}
        </div>
      )}

      {/* Current Status Card */}
      <div className="sub-currentPlan card">
        <div className="card-header">
          <h3 className="card-title">Your Subscription</h3>
        </div>
        <div className="sub-currentPlanBody">
          <div className="sub-currentPlanInfo">
            <span className="sub-currentTierName">
              {currentTierName}
            </span>
            <span className={getStatusBadgeClass()}>
              {getStatusLabel()}
            </span>
          </div>

          {/* Trial countdown */}
          {currentStatus === 'trialing' && getTrialDaysRemaining() !== null && (
            <div className="sub-trialCountdown">
              {getTrialDaysRemaining() === 0
                ? 'Your trial ends today'
                : getTrialDaysRemaining() === 1
                  ? '1 day remaining in your free trial'
                  : `${getTrialDaysRemaining()} days remaining in your free trial`
              }
            </div>
          )}

          {/* Renewal / cancellation notice */}
          {subscriptionStatus?.current_period_end && (
            <div className={`sub-renewalDate${cancelPending ? ' sub-renewalDate--canceling' : ''}`}>
              {cancelPending
                ? `Active until ${formatDate(subscriptionStatus.current_period_end)} · Will not renew`
                : currentStatus === 'trialing'
                  ? `First charge on ${formatDate(subscriptionStatus.current_period_end)}`
                  : `Renews on ${formatDate(subscriptionStatus.current_period_end)}`
              }
            </div>
          )}

          {/* Credit balance — daily + purchased (Account tab credit indicator copy) */}
          {isActive && !isPermanentTest && creditBalance?.daily && (
            <div className="sub-creditBalance">
              <span className="sub-creditBalanceItem">
                <strong>{creditBalance.daily.remaining ?? 0}</strong> / {creditBalance.daily.total ?? 0} daily credits
              </span>
              <span className="sub-creditBalanceItem">
                <strong>{creditBalance.purchased_credits ?? 0}</strong> purchased
                <span className="sub-creditBalanceNote"> (never expire)</span>
              </span>
            </div>
          )}

          {/* Action row: Manage Billing + Buy Credits + Cancel/Resume (Cancel pinned far-right) */}
          {isActive && !isPermanentTest && subscriptionStatus?.stripe_subscription_id && (
            <div className="sub-manageRow">
              <button
                className="btn btn-secondary sub-manageBtn"
                onClick={handleManageBilling}
                disabled={actionLoading}
              >
                {actionLoading ? 'Opening...' : 'Manage Billing'}
              </button>
              {canBuyCredits && (
                <button
                  className="btn btn-secondary sub-buyCreditsBtn"
                  onClick={() => setShowBuyCredits(true)}
                  disabled={actionLoading}
                >
                  Buy Credits
                </button>
              )}
              {currentStatus === 'trialing' && (
                <button
                  className="btn sub-subscribeNowBtn"
                  onClick={handleSubscribeNow}
                  disabled={actionLoading}
                >
                  {actionLoading ? 'Working...' : 'Subscribe Now'}
                </button>
              )}
              {cancelPending ? (
                <button
                  className="btn btn-secondary sub-resumeBtn"
                  onClick={handleResume}
                  disabled={actionLoading}
                >
                  {actionLoading ? 'Working...' : 'Resume Subscription'}
                </button>
              ) : (
                <button
                  className="btn btn-secondary sub-cancelBtn"
                  onClick={() => setCancelStep(1)}
                  disabled={actionLoading}
                >
                  Cancel Subscription
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Credit Usage & Costs — relocated from the old View My Plan modal (#43 UAT) */}
      {creditUsage && !creditUsage.is_test_account && creditUsage.daily_credits && (
        <div className="sub-creditUsage card">
          <div className="card-header"><h3 className="card-title">Credit Usage &amp; Costs</h3></div>
          <div className="sub-creditUsageBody">
          <div className="vpm-credits-summary">
            <div className="vpm-credits-big">{creditUsage.daily_credits.remaining || 0}</div>
            <div className="vpm-credits-sub">of {creditUsage.daily_credits.total || 0} daily credits remaining</div>
            <div className="vpm-credits-bar-lg">
              <div className="vpm-credits-fill-lg" style={{ width: `${(creditUsage.daily_credits.total || 0) > 0 ? Math.min(100, ((creditUsage.daily_credits.remaining || 0) / creditUsage.daily_credits.total) * 100) : 0}%` }} />
            </div>
            <div className="vpm-credits-reset">Resets at midnight UTC</div>
          </div>

          {creditUsage.actions_today?.length > 0 && (
            <div className="vpm-section">
              <h4 className="vpm-section-title">Today's Usage</h4>
              <table className="vpm-table">
                <thead><tr><th>Action</th><th>Uses</th><th>Credits</th></tr></thead>
                <tbody>
                  {creditUsage.actions_today.map(a => (
                    <tr key={a.type}><td>{a.label}</td><td>{a.count}</td><td>{a.credits_spent}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {creditUsage.available_actions && (
            <div className="vpm-section">
              <h4 className="vpm-section-title">AI Actions &amp; Credit Costs</h4>
              <table className="vpm-table">
                <thead><tr><th>Action</th><th>Credits</th><th>Access</th></tr></thead>
                <tbody>
                  {Object.entries(creditUsage.available_actions)
                    .sort((a, b) => a[1].credit_cost - b[1].credit_cost)
                    .map(([key, action]) => (
                      <tr key={key} className={!action.available ? 'vpm-locked' : ''}>
                        <td>{action.label}</td>
                        <td>{action.credit_cost}</td>
                        <td>{action.available ? <span className="vpm-check">Included</span> : <span className="vpm-lock">Upgrade</span>}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="vpm-tier-summary">
            {/* Trial is not purchasable so it has no plan entry — the number rides along on every
                plan as `trial_daily_credits`, derived server-side from AI_TIER_LIMITS. Do NOT
                hardcode it here again: the literal 45 that used to live on this line went stale
                the moment trial was raised to 90. */}
            {tiers[0]?.trial_daily_credits != null && (
              <div className="vpm-tier-row"><span className="vpm-tier-name">Trial</span><span>{tiers[0].trial_daily_credits} credits/day</span></div>
            )}
            {tiers.map(t => (
              <div key={t.key} className="vpm-tier-row">
                <span className="vpm-tier-name">{t.name} — ${typeof t.monthly_price === 'number' ? t.monthly_price.toFixed(2) : t.monthly_price}/mo</span>
                <span>{t.daily_credits} credits/day</span>
              </div>
            ))}
          </div>
          </div>
        </div>
      )}

      {/* Subscribe — tier options if not active */}
      {!isActive && !isPermanentTest && tiers.length > 0 && (
        <>
          {/* Billing Interval Toggle */}
          <div className="sub-billingToggle">
            <button
              className={`sub-toggleBtn ${billingInterval === 'monthly' ? 'sub-toggleBtn--active' : ''}`}
              onClick={() => setBillingInterval('monthly')}
            >
              Monthly
            </button>
            <button
              className={`sub-toggleBtn ${billingInterval === 'annual' ? 'sub-toggleBtn--active' : ''}`}
              onClick={() => setBillingInterval('annual')}
            >
              Annual
              <span className="sub-savingsBadge">Save 10%</span>
            </button>
          </div>

          {/* Tier cards: Base / Pro / Max */}
          <div className="sub-tierGrid">
            {tiers.map((tier) => (
              <div key={tier.key} className={`sub-tierCard${tier.popular ? ' sub-tierCard--recommended' : ''}`}>
                {tier.popular && <div className="sub-popularBadge">Most Popular</div>}
                <div className="sub-tierCardHeader">
                  <h3 className="sub-tierName">{tier.name}</h3>
                  <div className="sub-tierPrice">
                    <span className="sub-priceAmount">
                      ${billingInterval === 'monthly' ? tier.monthly_price.toFixed(2) : tier.annual_monthly_price.toFixed(2)}
                    </span>
                    <span className="sub-priceInterval">/mo</span>
                  </div>
                  {billingInterval === 'annual' && (
                    <div className="sub-annualTotal">
                      ${tier.annual_price.toFixed(2)}/year (billed annually)
                    </div>
                  )}
                  <div className="sub-trialNote">
                    {tier.trial_days}-day free trial &middot; Cancel anytime
                  </div>
                </div>
                <ul className="sub-featureList">
                  {tier.features.map((feature, idx) => (
                    <li key={idx} className="sub-featureItem">
                      <span className="sub-featureCheck">&#10003;</span>
                      {feature}
                    </li>
                  ))}
                  {(tier.not_included || []).map((feature, idx) => (
                    <li key={`x${idx}`} className="sub-featureItem sub-featureItem--excluded">
                      <span className="sub-featureCheck sub-featureCheck--x">&#10007;</span>
                      {feature}
                    </li>
                  ))}
                </ul>
                <div className="sub-tierCardFooter">
                  <button
                    className={`btn ${tier.popular ? 'btn-primary' : 'btn-secondary'} sub-upgradeBtn`}
                    onClick={() => handleSubscribeTier(tier)}
                    disabled={actionLoading}
                  >
                    {actionLoading ? 'Processing...' : 'Start Free Trial'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Referral Section */}
      {referralStats && (isActive || isPermanentTest) && (
        <div className="sub-referralCard card">
          <div className="card-header">
            <h3 className="card-title">Refer a Friend</h3>
          </div>
          <div className="sub-referralBody">
            <p className="sub-referralDescription">
              Share your referral code with a friend. They get a 14-day free trial
              (instead of 7) AND once they subscribe to any plan and stay subscribed for
              6 months, you earn a coupon good for 1 free month! Your friend must use this
              exact link directly in their browser to claim these benefits.
            </p>
            <div className="sub-referralCodeBox">
              <span className="sub-referralCode" id="sub-referralCodeText">{`${window.location.origin}/?ref=${referralStats.referral_code}`}</span>
              <button className="btn btn-secondary sub-copyBtn" onClick={copyReferralCode}>
                {copySuccess ? 'Copied!' : copyFailed ? 'Copy Failed — Link Selected' : 'Copy Registration Link to Clipboard'}
              </button>
            </div>
            {copyFailed && (
              <p className="sub-copyFallbackMsg" role="status">
                Your browser blocked the copy (this is common on iPhone and iPad). The link above is
                selected — press and hold, then choose <strong>Copy</strong>.
              </p>
            )}
            {(referralStats.total_invites > 0 || referralStats.converted > 0) && (
              <div className="sub-referralStats">
                <span className="sub-referralStat">
                  <strong>{referralStats.converted}</strong> converted
                </span>
                <span className="sub-referralStat">
                  <strong>{referralStats.pending_rewards}</strong> pending rewards
                </span>
                <span className="sub-referralStat">
                  <strong>{referralStats.redeemed_rewards}</strong> redeemed
                </span>
              </div>
            )}
            {referralStats.available_rewards?.some(r => r.is_available && !r.reward_redeemed) && (
              <button
                className="btn btn-primary sub-applyRewardBtn"
                onClick={handleApplyReward}
                disabled={actionLoading}
              >
                {actionLoading ? 'Applying...' : 'Apply Referral Reward'}
              </button>
            )}
            {referralStats.available_rewards?.length > 0 && (
              <div className="sub-referralEntries">
                <h4 className="sub-referralEntriesTitle">Your Referrals</h4>
                {referralStats.available_rewards.map((ref, idx) => (
                  <div key={idx} className="sub-referralEntry">
                    <span className="sub-referralEntryEmail">{ref.referee_email}</span>
                    <span className={`sub-referralEntryStatus sub-referralEntryStatus--${ref.status}`}>
                      {ref.reward_redeemed ? 'Rewarded' : ref.is_available ? 'Reward Ready' : 'Converted'}
                    </span>
                    {ref.valid_after && !ref.reward_redeemed && !ref.is_available && (
                      <span className="sub-referralEntryDate">
                        Reward available {formatDate(ref.valid_after)}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {/* Buy Credits modal */}
      {showBuyCredits && (
        <div className="modal-overlay" onClick={() => setShowBuyCredits(false)}>
          <div className="modal-content modal-content-md" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Buy Credits</h2>
              <button className="modal-close" onClick={() => setShowBuyCredits(false)}>&times;</button>
            </div>
            <div className="modal-body sub-buyCreditsBody">
              <p className="sub-buyCreditsIntro">
                Top up your account. <strong>Purchased credits never expire</strong> and are
                used only after your daily credits run out.
              </p>
              <div className="sub-creditPackGrid">
                {creditPacks.map((pack) => (
                  <div key={pack.key} className="sub-creditPack">
                    <div className="sub-creditPackCredits">{pack.credits} credits</div>
                    <div className="sub-creditPackPrice">${pack.price.toFixed(2)}</div>
                    <button
                      className="btn btn-primary sub-creditPackBtn"
                      onClick={() => handleBuyCredits(pack)}
                      disabled={actionLoading}
                    >
                      {actionLoading ? '...' : 'Buy'}
                    </button>
                  </div>
                ))}
              </div>
              <p className="sub-buyCreditsNote">
                You'll be taken to Stripe to complete your purchase securely.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Cancel confirmation — step 1 */}
      {cancelStep === 1 && (
        <div className="modal-overlay" onClick={() => setCancelStep(0)}>
          <div className="modal-content modal-content-md" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Cancel Subscription?</h2>
              <button className="modal-close" onClick={() => setCancelStep(0)}>&times;</button>
            </div>
            <div className="modal-body">
              <p>
                Cancel your <strong>{currentTierName}</strong> subscription? You'll keep full access
                {subscriptionStatus?.current_period_end
                  ? ` until ${formatDate(subscriptionStatus.current_period_end)}`
                  : ' until your paid period ends'} — you just won't be charged again.
              </p>
              <div className="sub-modalActions">
                <button className="btn btn-primary" onClick={() => setCancelStep(0)}>Keep My Plan</button>
                <button className="btn btn-secondary" onClick={() => setCancelStep(2)}>Continue to Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancel confirmation — step 2 (final) */}
      {cancelStep === 2 && (
        <div className="modal-overlay" onClick={() => setCancelStep(0)}>
          <div className="modal-content modal-content-md" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Are you sure?</h2>
              <button className="modal-close" onClick={() => setCancelStep(0)}>&times;</button>
            </div>
            <div className="modal-body">
              <p>
                This stops your subscription from renewing. You'd need to resubscribe to continue after
                {subscriptionStatus?.current_period_end
                  ? ` ${formatDate(subscriptionStatus.current_period_end)}`
                  : ' your paid period ends'}.
              </p>
              <div className="sub-modalActions">
                <button className="btn btn-primary" onClick={() => setCancelStep(0)}>Keep My Plan</button>
                <button
                  className="btn btn-secondary sub-cancelBtn"
                  onClick={handleCancel}
                  disabled={actionLoading}
                >
                  {actionLoading ? 'Canceling...' : 'Yes, Cancel'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SubscriptionTab;
